<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';

import Input from 'dashboard/components-next/input/Input.vue';
import PageLayout from 'dashboard/components-next/assistant/PageLayout.vue';
import DocumentCard from 'dashboard/components-next/assistant/shared/DocumentCard.vue';
import DocumentPageEmptyState from 'dashboard/components-next/assistant/shared/emptyStates/DocumentPageEmptyState.vue';
import CreateDocumentDialog from 'dashboard/components-next/assistant/pageComponents/document/CreateDocumentDialog.vue';
import DeleteDialog from 'dashboard/components-next/assistant/pageComponents/DeleteDialog.vue';

const store = useStore();
const { t } = useI18n();

const uiFlags = useMapGetter('assistantDocuments/getUIFlags');
const documents = useMapGetter('assistantDocuments/getRecords');
const documentsMeta = useMapGetter('assistantDocuments/getMeta');
const isFetching = computed(() => uiFlags.value.fetchingList);

const searchQuery = ref('');
const selectedDocument = ref(null);
const deleteDocumentDialog = ref(null);
const showCreateDialog = ref(false);
const createDocumentDialog = ref(null);

const fetchDocuments = (page = 1) => {
  const filterParams = { page };
  if (searchQuery.value.trim()) {
    filterParams.searchKey = searchQuery.value.trim();
  }
  store.dispatch('assistantDocuments/get', filterParams);
};

const debouncedSearch = debounce(() => fetchDocuments(1), 300);

const handleCreateDocument = () => {
  showCreateDialog.value = true;
  nextTick(() => createDocumentDialog.value.dialogRef.open());
};

const handleCreateDialogClose = () => {
  showCreateDialog.value = false;
};

const handleDelete = () => {
  deleteDocumentDialog.value.dialogRef.open();
};

const handleSync = async id => {
  try {
    await store.dispatch('assistantDocuments/sync', id);
    useAlert(t('ASSISTANT.DOCUMENTS.SYNC.QUEUED_MESSAGE'));
  } catch (error) {
    useAlert(t('ASSISTANT.DOCUMENTS.SYNC.ERROR_MESSAGE'));
  }
};

const handleAction = ({ action, id }) => {
  selectedDocument.value = documents.value.find(doc => id === doc.id);

  nextTick(() => {
    if (action === 'delete') {
      handleDelete();
    } else if (action === 'sync') {
      handleSync(id);
    }
  });
};

const onPageChange = page => fetchDocuments(page);

const onDeleteSuccess = () => {
  if (documents.value?.length === 0 && documentsMeta.value?.page > 1) {
    onPageChange(documentsMeta.value.page - 1);
  } else {
    fetchDocuments(documentsMeta.value?.page || 1);
  }
};

const onCreateSuccess = () => {
  fetchDocuments(1);
};

const hasActiveFilters = computed(() => Boolean(searchQuery.value.trim()));

onMounted(() => {
  fetchDocuments();
  store.dispatch('assistantAgents/get');
});
</script>

<template>
  <PageLayout
    :header-title="$t('ASSISTANT.DOCUMENTS.HEADER')"
    :button-label="$t('ASSISTANT.DOCUMENTS.ADD_NEW')"
    :button-policy="['administrator']"
    :total-count="documentsMeta.totalCount"
    :current-page="documentsMeta.page"
    :show-pagination-footer="!isFetching && !!documents.length"
    :is-fetching="isFetching"
    :is-empty="!documents.length && !hasActiveFilters"
    :feature-flag="FEATURE_FLAGS.AI_ASSISTANT"
    @update:current-page="onPageChange"
    @click="handleCreateDocument"
  >
    <template #search>
      <Input
        v-model="searchQuery"
        :placeholder="$t('ASSISTANT.DOCUMENTS.SEARCH_PLACEHOLDER')"
        class="max-w-64 min-w-0 w-full"
        size="sm"
        type="search"
        @input="debouncedSearch"
      />
    </template>

    <template #emptyState>
      <DocumentPageEmptyState @click="handleCreateDocument" />
    </template>

    <template #body>
      <div
        v-if="!documents.length && hasActiveFilters"
        class="flex flex-col items-center justify-center min-h-80 gap-2 text-center"
      >
        <span class="text-base font-medium text-n-slate-12">
          {{ $t('ASSISTANT.DOCUMENTS.EMPTY_STATE.FILTERED_TITLE') }}
        </span>
        <span class="max-w-md text-sm text-n-slate-11">
          {{ $t('ASSISTANT.DOCUMENTS.EMPTY_STATE.FILTERED_SUBTITLE') }}
        </span>
      </div>

      <div v-else class="flex flex-col gap-4">
        <DocumentCard
          v-for="doc in documents"
          :id="doc.id"
          :key="doc.id"
          :name="doc.name || doc.external_link"
          :external-link="doc.external_link || ''"
          :created-at="doc.created_at"
          :status="doc.status"
          :last-synced-at="doc.last_synced_at"
          :sync-in-progress="doc.status === 'in_progress'"
          @action="handleAction"
        />
      </div>
    </template>

    <CreateDocumentDialog
      v-if="showCreateDialog"
      ref="createDocumentDialog"
      @create-success="onCreateSuccess"
      @close="handleCreateDialogClose"
    />
    <DeleteDialog
      v-if="selectedDocument"
      ref="deleteDocumentDialog"
      :entity="selectedDocument"
      type="Documents"
      @delete-success="onDeleteSuccess"
    />
  </PageLayout>
</template>
